import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/AuthContext';

interface SidebarLinkProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({ to, icon, label, isActive }) => (
  <Link
    to={to}
    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
      isActive 
        ? 'bg-[#3A5E63] text-white' 
        : 'text-[#344854] hover:bg-[#E8F5F2]'
    }`}
  >
    <span className="text-xl">{icon}</span>
    <span>{label}</span>
  </Link>
);

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Get display name from user email or metadata
  const getUserDisplayName = () => {
    if (user?.email) {
      return user.email.split('@')[0];
    } else if (user?.user_metadata?.name) {
      return user.user_metadata.name;
    } else {
      return 'User';
    }
  };

  // Get first letter for avatar
  const getAvatarLetter = () => {
    const displayName = getUserDisplayName();
    return displayName.charAt(0).toUpperCase();
  };

  // Get user role from metadata
  const getUserRole = () => {
    return user?.app_metadata?.role || user?.user_metadata?.role || 'user';
  };

  return (
    <div className="flex h-[calc(100vh-5rem)] bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`fixed top-20 bottom-0 left-0 z-10 flex flex-col bg-white shadow-lg transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:relative md:top-auto md:bottom-auto md:h-full md:translate-x-0 md:w-64`}
      >
        {/* Admin Panel Title */}
        <div className="p-5 border-b">
          <div className="font-medium text-lg text-[#344854]">Admin Panel</div>
        </div>
        
        {/* Navigation links */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          <SidebarLink 
            to="/admin/dashboard" 
            icon={<span>📊</span>} 
            label="Dashboard" 
            isActive={location.pathname === '/admin/dashboard'} 
          />
          <SidebarLink 
            to="/admin/users" 
            icon={<span>👥</span>} 
            label="User Management" 
            isActive={location.pathname.startsWith('/admin/users')} 
          />
          <SidebarLink 
            to="/admin/content" 
            icon={<span>📝</span>} 
            label="Content Management" 
            isActive={location.pathname.startsWith('/admin/content')} 
          />
          <SidebarLink 
            to="/admin/team" 
            icon={<span>👨‍👩‍👧‍👦</span>} 
            label="Team Management" 
            isActive={location.pathname.startsWith('/admin/team')} 
          />
          <SidebarLink
            to="/admin/schedule"
            icon={<span>📅</span>}
            label="Scheduling"
            isActive={location.pathname.startsWith('/admin/schedule')}
          />
          <SidebarLink
            to="/admin/time-reports"
            icon={<span>⏱️</span>}
            label="Time Reports"
            isActive={location.pathname.startsWith('/admin/time-reports')}
          />
          <SidebarLink
            to="/admin/service-reports"
            icon={<span>💰</span>}
            label="Service Reports"
            isActive={location.pathname.startsWith('/admin/service-reports')}
          />
          <SidebarLink
            to="/admin/messages"
            icon={<span>💬</span>}
            label="Messages"
            isActive={location.pathname.startsWith('/admin/messages')}
          />
          <SidebarLink
            to="/admin/wallet-management"
            icon={<span>💳</span>}
            label="Wallet Management"
            isActive={location.pathname.startsWith('/admin/wallet-management')}
          />

          <div className="pt-4 border-t mt-8">
            <SidebarLink 
              to="/" 
              icon={<span>🏠</span>} 
              label="View Website" 
              isActive={false} 
            />
          </div>
        </nav>
        
        {/* User profile */}
        <div className="p-4 border-t">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#3A5E63] text-white flex items-center justify-center font-medium">
              {user && getAvatarLetter()}
            </div>
            <div className="flex-1">
              <div className="font-medium">{user && getUserDisplayName()}</div>
              <div className="text-xs text-[#344854]/70 capitalize">{user && getUserRole()}</div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile menu button for small screens */}
        <div className="md:hidden p-2 bg-white border-b flex items-center">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-[#344854] focus:outline-none"
            aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
          >
            <span className="text-xl">{sidebarOpen ? '✖️' : '☰'}</span>
          </button>
        </div>
        
        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 bg-gray-50">
          {children}
        </main>
      </div>
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-0 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default AdminLayout;
